import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import '../styles.css';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import heroImg from '../assets/programs/programs-hero.jpg';
import logoStamp from '../assets/chopstick-title-long.png';
import memberPhoto from '../assets/Sample/jacqueline-munguia-1pAwJiCD60c-unsplash.jpg';

// The team roster below is still placeholder data (Name1 / Role / lorem ipsum),
// so it stays hidden until the About us backend lands and real members are
// entered. Flip this back to true then -- see the image-upload spec, stage 4.
const SHOW_TEAM_SECTION = false;

// Placeholder bio — swap in real copy later.
const PLACEHOLDER_BIO =
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation.';

// Build a flat member list with numbered names so click/expand is easy to verify.
// `rows` keeps the design's centered pyramid layout for the default (collapsed) state.
function teamSlug(title) {
    return `team-${title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')}`;
}

function makeTeam(title, rowCounts, menuLabel) {
    let n = 1;
    const rows = rowCounts.map((count) =>
        Array.from({ length: count }, () => ({
            name: `Name${n++}`,
            role: 'Role',
            bio: PLACEHOLDER_BIO,
        })),
    );
    return {
        title,
        menuLabel: menuLabel ?? title,
        slug: teamSlug(title),
        rows,
        members: rows.flat(),
    };
}

const TEAMS = [
    makeTeam('Board of Directors', [3]),
    makeTeam(
        'Administration & Event operating Team',
        [1, 3],
        'Administration & Event operating',
    ),
    makeTeam('Illustration & Design Team', [1, 2], 'Illustration & Design'),
    makeTeam(
        'Media & Communications Team',
        [1, 3],
        'Media & Communications',
    ),
    makeTeam('Web development Team', [1, 3, 2], 'Web development'),
    makeTeam(
        'Storytelling & Content Team',
        [1, 2],
        'Storytelling & Content',
    ),
    makeTeam('Cultural music Ensemble', [1, 2, 3, 2], 'Cultural music Ensemble'),
];

function TeamMemberFigure({ photo, data, onClick, figureRef }) {
    return (
        <figure className="team-member" ref={figureRef} onClick={onClick}>
            <div className="team-member__photo">
                <img src={photo} alt="" loading="lazy" />
            </div>
            <figcaption>
                <span className="team-member__name">{data.name}</span>
                <span className="team-member__role">{data.role}</span>
            </figcaption>
        </figure>
    );
}

// FLIP timing shared by photo slides and the card-height expand.
const FLIP_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const FLIP_DURATION = 550;

function TeamCard({ id, title, members, rows, photo }) {
    // null = default pyramid; otherwise the index of the clicked member.
    const [active, setActive] = useState(null);
    const cardRef = useRef(null);
    const figureEls = useRef({});
    // Snapshots captured right before a click, used by the post-layout FLIP.
    const firstRects = useRef(null);
    const firstHeight = useRef(null);

    const handleClick = (i) => {
        const rects = {};
        Object.keys(figureEls.current).forEach((key) => {
            const el = figureEls.current[key];
            if (el) rects[key] = el.getBoundingClientRect();
        });
        firstRects.current = rects;
        firstHeight.current = cardRef.current
            ? cardRef.current.getBoundingClientRect().height
            : null;
        setActive((cur) => (cur === i ? null : i));
    };

    // After layout: slide each photo (FLIP) and ease the card height so the
    // widget itself grows/shrinks instead of jumping.
    useLayoutEffect(() => {
        const first = firstRects.current;
        const fromH = firstHeight.current;
        firstRects.current = null;
        firstHeight.current = null;
        if (!first) return;

        Object.keys(figureEls.current).forEach((key) => {
            const el = figureEls.current[key];
            const prev = first[key];
            if (!el || !prev) return;

            const last = el.getBoundingClientRect();
            const dx = prev.left - last.left;
            const dy = prev.top - last.top;
            if (!dx && !dy) return;

            el.style.transition = 'none';
            el.style.transform = `translate(${dx}px, ${dy}px)`;
            void el.offsetWidth;

            requestAnimationFrame(() => {
                el.style.transition = `transform ${FLIP_DURATION}ms ${FLIP_EASING}`;
                el.style.transform = '';
            });
        });

        const card = cardRef.current;
        if (card && fromH != null) {
            const toH = card.getBoundingClientRect().height;
            if (Math.abs(toH - fromH) > 0.5) {
                card.style.height = `${fromH}px`;
                card.style.overflow = 'hidden';
                void card.offsetWidth;

                requestAnimationFrame(() => {
                    card.style.transition = `height ${FLIP_DURATION}ms ${FLIP_EASING}`;
                    card.style.height = `${toH}px`;
                });

                const clear = () => {
                    card.style.height = '';
                    card.style.overflow = '';
                    card.style.transition = '';
                    card.removeEventListener('transitionend', clear);
                };
                card.addEventListener('transitionend', clear);
            }
        }
    }, [active]);

    const setFigureRef = (i) => (el) => {
        if (el) figureEls.current[i] = el;
        else delete figureEls.current[i];
    };

    return (
        <article
            id={id}
            className="team-card team-card--interactive"
            ref={cardRef}
        >
            {/* Brown ornament lines (Figma Line 24/25, #6E4E37, 4px). */}
            <div
                className="team-card__rule team-card__rule--top"
                id={`${id}-top`}
                aria-hidden
            />

            <h3 className="team-card__title">{title}</h3>

            {active === null ? (
                <div className="team-card__grid">
                    {rows.map((rowMembers, ri) => (
                        <div className="team-row" key={ri}>
                            {rowMembers.map((m) => {
                                const i = members.indexOf(m);
                                return (
                                    <TeamMemberFigure
                                        key={i}
                                        photo={photo}
                                        data={m}
                                        figureRef={setFigureRef(i)}
                                        onClick={() => handleClick(i)}
                                    />
                                );
                            })}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="team-detail">
                    <div className="team-detail__top">
                        <TeamMemberFigure
                            photo={photo}
                            data={members[active]}
                            figureRef={setFigureRef(active)}
                            onClick={() => handleClick(active)}
                        />
                        <div className="team-detail__desc" key={active}>
                            <h4 className="team-detail__name">
                                {members[active].name}
                            </h4>
                            <p className="team-detail__role">
                                {members[active].role}
                            </p>
                            <p className="team-detail__text">
                                {members[active].bio}
                            </p>
                        </div>
                    </div>

                    <div className="team-row team-detail__rest">
                        {members
                            .map((m, i) => ({ m, i }))
                            .filter(({ i }) => i !== active)
                            .map(({ m, i }) => (
                                <TeamMemberFigure
                                    key={i}
                                    photo={photo}
                                    data={m}
                                    figureRef={setFigureRef(i)}
                                    onClick={() => handleClick(i)}
                                />
                            ))}
                    </div>
                </div>
            )}

            <div className="team-card__rule team-card__rule--bottom" aria-hidden />
        </article>
    );
}

export default function About() {
    const [menuOpen, setMenuOpen] = useState(false);
    // 'right' = flows right from the toggle (preferred); 'left' = the original
    // right-aligned placement, used when the viewport has no room on the right.
    const [menuAlign, setMenuAlign] = useState('left');
    const toggleRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, []);

    useEffect(() => {
        if (!menuOpen) return;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setMenuOpen(false);
        };

        const handlePointerDown = (event) => {
            const target = event.target;
            if (
                menuRef.current?.contains(target) ||
                toggleRef.current?.contains(target)
            ) {
                return;
            }
            setMenuOpen(false);
        };

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('pointerdown', handlePointerDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('pointerdown', handlePointerDown);
        };
    }, [menuOpen]);

    // Choose the dropdown side before paint: open to the right of the toggle
    // when it fits the viewport, otherwise keep the left-flowing fallback.
    useLayoutEffect(() => {
        if (!menuOpen) return;

        const measure = () => {
            const toggle = toggleRef.current;
            const menu = menuRef.current;
            if (!toggle || !menu) return;

            // Menu width depends only on the viewport, so measuring it while
            // it sits on either side can't feed back into the decision.
            const GUTTER = 16;
            const room =
                document.documentElement.clientWidth -
                toggle.getBoundingClientRect().left;
            setMenuAlign(menu.offsetWidth + GUTTER <= room ? 'right' : 'left');
        };

        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [menuOpen]);

    const scrollToTeam = (slug) => {
        setMenuOpen(false);

        // Wait for menu close paint so layout/position is stable before scrolling.
        requestAnimationFrame(() => {
            const line =
                document.getElementById(`${slug}-top`) ??
                document.getElementById(slug)?.querySelector(
                    '.team-card__rule--top',
                );
            if (!line) return;

            const headerHeight =
                document.querySelector('header')?.offsetHeight ?? 0;
            const scrollY =
                window.scrollY || document.documentElement.scrollTop;
            const lineTop = line.getBoundingClientRect().top + scrollY;
            const top = lineTop - headerHeight;

            window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        });
    };

    useEffect(() => {
        document.title = 'About Us - CHOPSTICKSTORY';

        const setMeta = (name, content, prop = false) => {
            const selector = prop
                ? `meta[property="${name}"]`
                : `meta[name="${name}"]`;
            let el = document.querySelector(selector);
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute(prop ? 'property' : 'name', name);
                document.head.appendChild(el);
            }
            el.content = content;
        };

        setMeta(
            'description',
            'Learn about CHOPSTICKSTORY, a non-profit organization dedicated to sharing Korean culture.',
        );
        setMeta(
            'keywords',
            'CHOPSTICKSTORY, Korean culture, non-profit organization, cultural education, team',
        );
        setMeta('author', 'CHOPSTICKSTORY');
        setMeta('og:title', 'About Us - CHOPSTICKSTORY', true);
        setMeta(
            'og:description',
            'Discover our mission to foster cultural understanding through Korean cultural education.',
            true,
        );
    }, []);

    return (
        <div className="about-page">
            <PageHero image={heroImg} title="About us" />

            <main className="about-main">
                <section className="about-intro" aria-labelledby="about-lead">
                    <img
                        className="about-intro__logo"
                        src={logoStamp}
                        alt="CHOPSTICKSTORY"
                    />
                    <p className="about-intro__lead" id="about-lead">
                        We are a non-profit organization committed to fostering
                        understanding of Korean culture through engaging programs
                        and inspiring stories.
                    </p>
                </section>

                <section className="about-mv" aria-label="Mission and vision">
                    <div className="about-mv__col">
                        <h2 className="about-mv__title">Our Mission</h2>
                        <p>
                            Our mission is dedicated to fostering harmony and
                            unity within communities by sharing and celebrating
                            Korean culture.
                            <br />
                            <br />
                            Through our programs, we aim to inspire cultural
                            appreciation and mutual understanding, paving the way
                            for a more connected and inclusive society.
                        </p>
                    </div>

                    <div className="about-mv__col">
                        <h2 className="about-mv__title">Our Vision</h2>
                        <p>
                            Our vision is to contribute to building a better
                            world by promoting cultural connection, empathy, and
                            collaboration.
                            <br />
                            <br />
                            By embracing the values and traditions of Korean
                            culture, CHOPSTICKSTORY aspires to create a future
                            where diverse communities thrive together in harmony.
                        </p>
                    </div>
                </section>

                {SHOW_TEAM_SECTION && (
                <section className="about-team" id="team">
                    <div className="about-team__divider" aria-hidden />

                    <div className="about-team__panel">
                        <div className="about-team__header">
                            <h2 className="about-team__heading">
                                Meet Our Team
                            </h2>

                            <div className="about-team__nav">
                                <button
                                    type="button"
                                    ref={toggleRef}
                                    className={
                                        'team-nav-toggle' +
                                        (menuOpen ? ' is-open' : '')
                                    }
                                    aria-expanded={menuOpen}
                                    aria-controls="team-nav-menu"
                                    aria-label="Open team navigation menu"
                                    onClick={() =>
                                        setMenuOpen((open) => !open)
                                    }
                                >
                                    <span aria-hidden="true" />
                                    <span aria-hidden="true" />
                                </button>
                            </div>

                            <nav
                                id="team-nav-menu"
                                ref={menuRef}
                                className={
                                    'team-nav-menu' +
                                    (menuAlign === 'right'
                                        ? ' team-nav-menu--right'
                                        : '') +
                                    (menuOpen ? ' is-open' : '')
                                }
                                aria-hidden={!menuOpen}
                                aria-label="Team sections"
                            >
                                <ul className="team-nav-menu__list">
                                    {TEAMS.map((team) => (
                                        <li key={team.slug}>
                                            <button
                                                type="button"
                                                className="team-nav-menu__item"
                                                onClick={() =>
                                                    scrollToTeam(team.slug)
                                                }
                                            >
                                                {team.menuLabel}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        </div>

                        {TEAMS.map((team) => (
                            <TeamCard
                                key={team.slug}
                                id={team.slug}
                                title={team.title}
                                members={team.members}
                                rows={team.rows}
                                photo={memberPhoto}
                            />
                        ))}
                    </div>
                </section>
                )}
            </main>

            <Footer />
        </div>
    );
}
